import React, { useEffect, useMemo, useRef } from 'react';

import merge from 'lodash/merge';
import { useIntl } from 'react-intl-next';
import { v4 as uuid } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import LegacyButton from '@atlaskit/button/standard-button';
import { DateTimePicker } from '@atlaskit/datetime-picker';
import ArrowLeftCircleIcon from '@atlaskit/icon/core/migration/arrow-left--arrow-left-circle';
import InlineMessage from '@atlaskit/inline-message';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { RadioGroup } from '@atlaskit/radio';
import Select from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { fireTrackAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { DayTimeInterval } from '@atlassian/eoc-day-time-interval';
import { Flex } from '@atlassian/eoc-flex';
import { ErrorMessage, EocField as Field, EocForm as Form } from '@atlassian/eoc-form';
import {
	type RequestedResponder,
	commonMessages as scheduleCommonMessages,
} from '@atlassian/eoc-schedule-common';
import { useSpotlightActions } from '@atlassian/eoc-spotlight-store';

import {
	DEFAULT_TIMES,
	ROTATION_NAME_MAX_LENGTH,
	SPOTLIGHT_NAMES,
	SPOTLIGHT_TOURS,
} from '../../../../../common/constants';
import { messages as commonMessages } from '../../../../../common/messages';
import {
	type DayTimeRange,
	type OptionField,
	RotationType,
	ShiftFrequency,
	type TimeRange,
	TimingType,
} from '../../../../../common/types';
import { FieldCondition } from '../../../../../common/ui/field-condition';
import { FieldError } from '../../../../../common/ui/field-error';
import { Container } from '../../../../../common/ui/side-container/styled';
import { TimeInterval } from '../../../../../common/ui/time-interval';
import {
	useInitialValues,
	useRotationActions,
	useRotationOptions,
} from '../../../../../controllers/edit';
import { useDetailVariant } from '../../../../../controllers/edit-store/helpers';
import { RotationDetailVariant } from '../../../../../controllers/edit-store/state/types';
import {
	DEFAULT_SHIFT_PERIOD,
	INITIAL_CUSTOM_RESTRICTIONS,
	VALID_SHIFT_RANGE,
} from '../../../../../controllers/edit/constants';
import {
	type FormFields,
	HackStatus,
	type OriginalBeforeRecipientUpdate,
	type TemplateOption,
} from '../../../../../controllers/edit/types';
import {
	downsizeRestriction,
	expandRestriction,
	generateDefaultEnd,
	getNextInterval,
	rearrageParticipants,
} from '../../../../../controllers/edit/utils';
import { useActions as usePageActions } from '../../../../../controllers/page-store';
import { DiscardVariant } from '../../../../../controllers/page-store/state/types';
import { useSchedule } from '../../../../../controllers/schedule-store/helpers';
import { formatting } from '../../../../../utils/date';
import { createFormFocusDecorator } from '../../../../../utils/form';
import { isEmpty } from '../../../../../utils/validators';

import { messages } from './messages';
import { OnChange } from './on-change';
import { ParticipantList } from './participant-list';
import { ChangeEvent } from './participant-list/types';
import {
	Footer,
	Group,
	Info,
	MessageContainer,
	NumberContainer,
	Scrollable,
	Shift,
	ShiftDetails,
	Timings,
} from './styled';
import { TemplateSelect } from './template-select';
import { TimingStatus } from './types';
import { getTimingStatus } from './utils';

const buttonContainerStyles = xcss({
	marginTop: 'space.negative.100',
});
const backButtonContainerStyles = xcss({
	marginBottom: 'space.200',
});

// TODO: add backups for restrictions and shift period
// TODO: need onBlur and onFocus integration for DayTimeInterval component for validateOnBlur
const EditRotation = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intlApi = useIntl();
	const { trigger } = useSpotlightActions();

	const [{ id: scheduleId }] = useSchedule();

	const { timingOptions, templateOptions, shiftTypeOptions, shiftFrequencyOptions } =
		useRotationOptions();
	const [rotationId, initialValues, recipientUpdate] = useInitialValues();
	const [rotationDetailVariant] = useDetailVariant();
	const { openDiscardChanges } = usePageActions();
	const { add, edit } = useRotationActions(scheduleId, rotationId);

	const original = useRef<OriginalBeforeRecipientUpdate>({});

	const focusDecorator = useMemo(() => createFormFocusDecorator<FormFields>(), []);

	useEffect(() => {
		trigger(SPOTLIGHT_TOURS.rotationEdit);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Form<FormFields>
				initialValues={initialValues}
				// NOTE: not allowing initial values to overwrite
				initialValuesEqual={() => true}
				decorators={[focusDecorator]}
				subscription={{ submitting: true, hasValidationErrors: true }}
				// TODO: Add async validation -> Do all validations here and updateRotation -> remove OnChange component
				onSubmit={async (values) => {
					const event = createAnalyticsEvent({});
					const eventPayload = {
						nameChange: initialValues?.name === values.name,
						participantsChange:
							JSON.stringify(initialValues?.participants) === JSON.stringify(values.participants),
						timingChange:
							initialValues?.startTime === values.startTime ||
							initialValues?.endTime === values.endTime,
					};

					switch (rotationDetailVariant) {
						case RotationDetailVariant.Edit:
							return edit(values, {
								onSuccess: () =>
									fireTrackAnalytics(event, 'rotationUpdate succeeded', eventPayload),
								onError: () => () =>
									fireTrackAnalytics(event, 'rotationUpdate failed', eventPayload),
							});
						case RotationDetailVariant.Add:
						case RotationDetailVariant.Clone:
							return add(values, {
								onSuccess: () =>
									fireTrackAnalytics(event, 'rotationUpdate succeeded', eventPayload),
								onError: () => () =>
									fireTrackAnalytics(event, 'rotationUpdate failed', eventPayload),
							});
					}
				}}
			>
				{({ form, submitting, handleSubmit }) => {
					return (
						<SpotlightTarget name={SPOTLIGHT_NAMES.editRotationForm}>
							<Container>
								<Box xcss={backButtonContainerStyles}>
									<Button
										id="back-button"
										iconBefore={ArrowLeftCircleIcon}
										onClick={() =>
											openDiscardChanges({
												variant: DiscardVariant.Discard,
												id: 'back-button',
											})
										}
										appearance="subtle"
									>
										{intlApi.formatMessage(messages.back)}
									</Button>
								</Box>

								<Scrollable>
									<Field<string>
										name="name"
										label={intlApi.formatMessage(messages.rotationName)}
										isRequired
										validate={(value) => {
											if (isEmpty(value)) {
												return intlApi.formatMessage(commonMessages.required);
											}

											if (value.startsWith(' ') || value.endsWith(' ')) {
												return intlApi.formatMessage(commonMessages.noSpaceAtEndOrStart);
											}

											if (value.length > ROTATION_NAME_MAX_LENGTH) {
												return intlApi.formatMessage(commonMessages.maxLength, {
													max: ROTATION_NAME_MAX_LENGTH,
												});
											}
										}}
									>
										{({ input, meta }) => (
											<>
												<Textfield
													// FIX: Focus problem (isDisabled)
													{...input}
													{...(submitting && { isDisabled: submitting })}
													autoComplete="off"
												/>
												{meta.error && (
													<ErrorMessage {...({ fieldId: 'name' } as any)}>
														{meta.error}
													</ErrorMessage>
												)}
											</>
										)}
									</Field>

									<FieldCondition<HackStatus> when="hackStatus" is={HackStatus.Applied}>
										<MessageContainer>
											<InlineMessage
												{...(submitting && { isDisabled: submitting })}
												appearance="confirmation"
												title={intlApi.formatMessage(messages.participantListStartUpdated)}
											>
												<Flex flexDirection="column" rowGap={1}>
													<Text as="p">{intlApi.formatMessage(messages.preserveOnCallOrder)}</Text>
													<LegacyButton
														{...(submitting && { isDisabled: submitting })}
														appearance="link"
														onClick={() => {
															form.batch(() => {
																form.change('hackStatus', HackStatus.Rejected);
																form.change(
																	'startTime',
																	original.current.startTime ?? initialValues?.startTime,
																);
																form.change(
																	'participants',
																	original.current.participants ?? initialValues?.participants,
																);
															});
														}}
													>
														{intlApi.formatMessage(messages.undoAutomaticChanges)}
													</LegacyButton>
												</Flex>
											</InlineMessage>
										</MessageContainer>
									</FieldCondition>

									<Field<RequestedResponder<true, true>[]>
										name="participants"
										label={intlApi.formatMessage(commonMessages.participants)}
									>
										{({ input }) => (
											<ParticipantList
												{...input}
												{...(submitting && { isDisabled: submitting })}
												value={input.value.map((responder) =>
													merge({}, responder, {
														key: 'key' in responder ? (responder.key as string) : uuid(),
													}),
												)}
												onChange={(value, type) => {
													// TODO: Behavior for replace with going to be weird and possible wrong.
													// This is on purpose. We need to re-think about shifted responders for JSM use cases.
													// Behavior for other actions are same as OpsGenie

													const hackStatus = form.getState().values.hackStatus;

													const hackable =
														recipientUpdate?.shiftable && hackStatus === HackStatus.None;

													const ignored = [ChangeEvent.Order, ChangeEvent.Replace].some(
														(t) => type === t,
													);

													if (hackable && !ignored) {
														original.current.participants = value;
														original.current.startTime = form.getState().values.startTime;

														form.batch(() => {
															form.change('hackStatus', HackStatus.Applied);
															form.change('startTime', recipientUpdate.startTime);
															form.change(
																'participants',
																rearrageParticipants(value, recipientUpdate.responders),
															);
														});

														return;
													}

													form.change('participants', value);
												}}
											/>
										)}
									</Field>

									<Timings>{intlApi.formatMessage(messages.timings)}</Timings>
									<Field<TemplateOption>
										name="template"
										label={intlApi.formatMessage(messages.selectShiftDuration)}
									>
										{({ input }) => (
											<TemplateSelect
												{...input}
												{...(submitting && { isDisabled: submitting })}
												onChange={(template) => {
													if (template) {
														form.batch(() => {
															const timingType = template.restrictions.length
																? template.timingType
																: null;

															form.change('template', template);
															form.change('timingType', timingType);
															form.change('restrictions', template.restrictions);
														});
													}
												}}
												options={templateOptions}
												placeholder={
													getTimingStatus(form.getState().values.template) !== TimingStatus.Custom
														? intlApi.formatMessage(commonMessages.select)
														: intlApi.formatMessage(messages.customTimings)
												}
												onCustomize={() => {
													form.batch(() => {
														form.change('template', null);
														form.change('timingType', TimingType.Customday);
														form.change('restrictions', INITIAL_CUSTOM_RESTRICTIONS);
													});
												}}
											/>
										)}
									</Field>

									<FieldCondition<TemplateOption, TimingStatus>
										when="template"
										not={TimingStatus.NoRestriction}
										transform={(template) => getTimingStatus(template)}
									>
										<>
											<Field<TimingType> name="timingType">
												{({ input }) => (
													<Group>
														<RadioGroup
															{...input}
															{...(submitting && { isDisabled: submitting })}
															onChange={(value) => {
																const timing = value.target.value;

																if (timing === TimingType.Everday) {
																	form.batch(() => {
																		form.change('template', null);
																		form.change('timingType', TimingType.Everday);
																		form.change(
																			'restrictions',
																			downsizeRestriction(form.getState().values.restrictions),
																		);
																	});
																}

																if (timing === TimingType.Customday) {
																	form.batch(() => {
																		form.change('template', null);
																		form.change('timingType', TimingType.Customday);
																		form.change(
																			'restrictions',
																			expandRestriction(form.getState().values.restrictions),
																		);
																	});
																}
															}}
															options={timingOptions}
														/>
													</Group>
												)}
											</Field>

											<FieldCondition<TimingType> when="timingType" is={TimingType.Everday}>
												<Field<DayTimeRange[]>
													name="restrictions"
													validate={(value: TimeRange[]) => {
														const range = value[0];

														if (isEmpty(range)) {
															return intlApi.formatMessage(commonMessages.required);
														}

														if (
															range.startHour === range.endHour &&
															range.startMinute === range.endMinute
														) {
															return intlApi.formatMessage(commonMessages.startEndSame);
														}

														if (
															range.startMinute !== 30 &&
															range.startMinute !== 0 &&
															range.endMinute !== 30 &&
															range.endMinute !== 0
														) {
															return intlApi.formatMessage(commonMessages.thirtyMinuteInterval);
														}
													}}
												>
													{({ input, meta }) => (
														<>
															<TimeInterval
																{...input}
																{...(submitting && { isDisabled: submitting })}
																isInvalid={meta.error}
																value={input.value[0]}
																onChange={(timeRange) => {
																	form.batch(() => {
																		if (form.getState().values.template) {
																			form.change('template', null);
																		}
																		// WARNING: invalid assertion (but will not break anything)
																		form.change('restrictions', [timeRange] as DayTimeRange[]);
																	});
																}}
																labels={{
																	start: intlApi.formatMessage(scheduleCommonMessages.from),
																	end: intlApi.formatMessage(scheduleCommonMessages.to),
																}}
															/>
															{meta.error && (
																<ErrorMessage {...({ fieldId: 'restrictions' } as any)}>
																	{meta.error}
																</ErrorMessage>
															)}
														</>
													)}
												</Field>
											</FieldCondition>

											<FieldCondition<TimingType> when="timingType" is={TimingType.Customday}>
												<Field<DayTimeRange[]>
													name="restrictions"
													validate={(value) => {
														// NOTE: This is temporary validation function
														// Replace with updated DayTimeIntervalField component
														for (const range of value) {
															if (isEmpty(range)) {
																return intlApi.formatMessage(commonMessages.required);
															}

															if (
																range.startDay === range.endDay &&
																range.startHour === range.endHour &&
																range.startMinute === range.endMinute
															) {
																return intlApi.formatMessage(commonMessages.startEndSame);
															}

															if (
																range.startMinute !== 30 &&
																range.startMinute !== 0 &&
																range.endMinute !== 30 &&
																range.endMinute !== 0
															) {
																return intlApi.formatMessage(commonMessages.thirtyMinuteInterval);
															}
														}
													}}
												>
													{({ input, meta }) => (
														<>
															<DayTimeInterval
																{...input}
																{...(submitting && { isDisabled: submitting })}
																variant="compact"
																newIntervalValue={getNextInterval(input.value)}
																onChange={(dayTimeRange) => {
																	form.batch(() => {
																		form.change('template', null);
																		// WARNING: invalid assertion (but will not break anything)
																		form.change('restrictions', dayTimeRange as DayTimeRange[]);
																	});
																}}
																labels={{
																	start: intlApi.formatMessage(scheduleCommonMessages.from),
																	end: intlApi.formatMessage(scheduleCommonMessages.to),
																}}
															/>
															{meta.error && (
																<ErrorMessage {...({ fieldId: 'restrictions' } as any)}>
																	{meta.error}
																</ErrorMessage>
															)}
														</>
													)}
												</Field>
											</FieldCondition>
										</>
									</FieldCondition>

									<Field<OptionField<ShiftFrequency>>
										name="shiftFrequency"
										label={intlApi.formatMessage(messages.shiftFrequency)}
										isRequired
									>
										{({ input }) => (
											<>
												<Shift>
													<Select<OptionField<ShiftFrequency>, false>
														{...input}
														{...(submitting && { isDisabled: submitting })}
														onChange={(option) => {
															if (isEmpty(option)) {
																return;
															}

															const frequency = option.value;

															if (frequency === ShiftFrequency.Custom) {
																form.batch(() => {
																	form.change('shiftFrequency', option);
																	form.change('shiftPeriod', DEFAULT_SHIFT_PERIOD);
																	form.change(
																		'shiftType',
																		shiftTypeOptions.find(
																			({ value }) => value === RotationType.DAILY,
																		),
																	);
																});
															}

															if (frequency === ShiftFrequency.Daily) {
																form.batch(() => {
																	form.change('shiftFrequency', option);
																	form.change('shiftPeriod', DEFAULT_SHIFT_PERIOD);
																	form.change(
																		'shiftType',
																		shiftTypeOptions.find(
																			({ value }) => value === RotationType.DAILY,
																		),
																	);
																});
															}

															if (frequency === ShiftFrequency.Weekly) {
																form.batch(() => {
																	form.change('shiftFrequency', option);
																	form.change('shiftPeriod', DEFAULT_SHIFT_PERIOD);
																	form.change(
																		'shiftType',
																		shiftTypeOptions.find(
																			({ value }) => value === RotationType.WEEKLY,
																		),
																	);
																});
															}
														}}
														options={shiftFrequencyOptions}
													/>

													{input?.value?.value === ShiftFrequency.Custom && (
														<ShiftDetails>
															<Field<string>
																name="shiftPeriod"
																validate={(value) => {
																	if (isEmpty(value)) {
																		return intlApi.formatMessage(commonMessages.minValue);
																	}

																	const num = parseInt(value);

																	const { max, min } =
																		VALID_SHIFT_RANGE[form.getState().values.shiftType.value];

																	if (num > max) {
																		return intlApi.formatMessage(commonMessages.maxValue, { max });
																	}

																	if (num < min) {
																		return intlApi.formatMessage(commonMessages.minValue);
																	}
																}}
															>
																{({ input, meta }) => (
																	<NumberContainer>
																		<Textfield
																			// FIX: Focus problem (isDisabled)
																			{...input}
																			{...(submitting && {
																				isDisabled: submitting,
																			})}
																			isInvalid={meta.error}
																			autoComplete="off"
																			min={
																				VALID_SHIFT_RANGE[form.getState().values.shiftType.value]
																					.min
																			}
																			max={
																				VALID_SHIFT_RANGE[form.getState().values.shiftType.value]
																					.max
																			}
																			onChange={(event) => {
																				const target = event.target as HTMLInputElement;
																				target.value = target.value.replace(/[^0-9]/g, '');
																				if (target.value.length > 1) {
																					target.value = target.value.replace(/^0+/, '');
																				}
																				input.onChange(event);
																			}}
																		/>
																	</NumberContainer>
																)}
															</Field>

															<Field<OptionField<RotationType>> name="shiftType">
																{({ input }) => (
																	<Select<OptionField<RotationType>, false>
																		{...input}
																		{...(submitting && {
																			isDisabled: submitting,
																		})}
																		onChange={(option) => {
																			if (isEmpty(option)) {
																				return;
																			}

																			form.batch(() => {
																				form.change('shiftType', option);
																				form.change('shiftPeriod', DEFAULT_SHIFT_PERIOD);
																			});
																		}}
																		options={shiftTypeOptions}
																	/>
																)}
															</Field>
														</ShiftDetails>
													)}
												</Shift>

												<FieldError name="shiftPeriod" />
											</>
										)}
									</Field>

									<Field<string>
										name="startTime"
										label={intlApi.formatMessage(commonMessages.startTime)}
										isRequired
										validate={(value) => {
											if (isEmpty(value)) {
												return intlApi.formatMessage(commonMessages.required);
											}

											if (
												new Date(value).getMinutes() !== 0 &&
												new Date(value).getMinutes() !== 30
											) {
												return intlApi.formatMessage(commonMessages.thirtyMinuteInterval);
											}

											if (!form.getState().values.hasEndTime) {
												return;
											}

											if (
												new Date(value).valueOf() >
												new Date(form.getState().values.endTime!).valueOf()
											) {
												return intlApi.formatMessage(commonMessages.startLaterEnd);
											}

											if (
												new Date(value).valueOf() ===
												new Date(form.getState().values.endTime!).valueOf()
											) {
												return intlApi.formatMessage(commonMessages.startEndSame);
											}
										}}
									>
										{({ input, meta }) => (
											<>
												<DateTimePicker
													// FIX: Focus problem (isDisabled)
													{...input}
													{...(submitting && { isDisabled: submitting })}
													isInvalid={meta.error}
													datePickerProps={{
														weekStartDay: 1,
														label: intlApi
															.formatMessage(messages.rotation, {
																label: intlApi.formatMessage(commonMessages.startDate),
															})
															.toLowerCase(),
													}}
													timePickerProps={{
														times: DEFAULT_TIMES,
														// NOTE: limits 30 minutes intervals
														timeIsEditable: false,
														label: intlApi
															.formatMessage(messages.rotation, {
																label: intlApi.formatMessage(commonMessages.startTime),
															})
															.toLowerCase(),
													}}
												/>
												{meta.error && (
													<ErrorMessage {...({ fieldId: 'startTime' } as any)}>
														{meta.error}
													</ErrorMessage>
												)}
												<Info>{intlApi.formatMessage(messages.timesInScheduleTimezone)}</Info>
											</>
										)}
									</Field>

									<FieldCondition<boolean> when="hasEndTime" is={true}>
										<Field<string>
											name="endTime"
											label={intlApi.formatMessage(commonMessages.endTime)}
											isRequired
											validate={(value) => {
												if (isEmpty(value)) {
													return intlApi.formatMessage(commonMessages.required);
												}

												if (
													new Date(value).getMinutes() !== 0 &&
													new Date(value).getMinutes() !== 30
												) {
													return intlApi.formatMessage(commonMessages.thirtyMinuteInterval);
												}

												if (
													new Date(value).valueOf() <
													new Date(form.getState().values.startTime).valueOf()
												) {
													return intlApi.formatMessage(commonMessages.endEarlierStart);
												}

												if (
													new Date(value).valueOf() ===
													new Date(form.getState().values.startTime!).valueOf()
												) {
													return intlApi.formatMessage(commonMessages.startEndSame);
												}
											}}
										>
											{({ input, meta }) => (
												<>
													<DateTimePicker
														// FIX: Focus problem (isDisabled)
														{...input}
														{...(submitting && { isDisabled: submitting })}
														isInvalid={meta.error}
														datePickerProps={{
															weekStartDay: 1,
															label: intlApi
																.formatMessage(messages.rotation, {
																	label: intlApi.formatMessage(commonMessages.endDate),
																})
																.toLowerCase(),
														}}
														timePickerProps={{
															times: DEFAULT_TIMES,
															// NOTE: limits 30 minutes intervals
															timeIsEditable: false,
															label: intlApi
																.formatMessage(messages.rotation, {
																	label: intlApi.formatMessage(commonMessages.endTime),
																})
																.toLowerCase(),
														}}
													/>
													{meta.error && (
														<ErrorMessage {...({ fieldId: 'endTime' } as any)}>
															{meta.error}
														</ErrorMessage>
													)}
													<Info>{intlApi.formatMessage(messages.timesInScheduleTimezone)}</Info>
												</>
											)}
										</Field>
									</FieldCondition>

									<Field name="hasEndTime">
										{({ input: { value: hasEndTime } }) => (
											<Box xcss={buttonContainerStyles}>
												<LegacyButton
													{...(submitting && { isDisabled: submitting })}
													appearance="link"
													onClick={() => {
														if (hasEndTime) {
															form.batch(() => {
																form.change('hasEndTime', false);
															});
														} else {
															form.batch(() => {
																form.change('hasEndTime', true);
																form.change(
																	'endTime',
																	formatting.toISO(
																		generateDefaultEnd(form.getState().values.startTime),
																	),
																);
															});
														}
													}}
												>
													{hasEndTime
														? intlApi.formatMessage(messages.removeEndTime)
														: intlApi.formatMessage(messages.defineEndTime)}
												</LegacyButton>
											</Box>
										)}
									</Field>
								</Scrollable>

								<OnChange />

								<Footer>
									<Button
										appearance="primary"
										isLoading={submitting}
										onClick={handleSubmit}
										type="button"
									>
										{intlApi.formatMessage(
											rotationDetailVariant === RotationDetailVariant.Edit
												? messages.updateRotation
												: messages.addRotation,
										)}
									</Button>
									<Button
										{...(submitting && { isDisabled: submitting })}
										id="discard-button"
										appearance="default"
										onClick={(_, analyticsEvent) => {
											fireUIAnalytics(analyticsEvent, 'editModeDiscardButton');

											openDiscardChanges({
												variant: DiscardVariant.Discard,
												id: 'discard-button',
											});
										}}
									>
										{intlApi.formatMessage(commonMessages.discard)}
									</Button>
								</Footer>
							</Container>
						</SpotlightTarget>
					);
				}}
			</Form>
		</>
	);
};

export { EditRotation };
